import React from "react";
import PersonAvatar from "./PersonAvatar";
import { getEmployeeMainRole } from "./employeeMainRole";

import LockIcon from "@mui/icons-material/Lock";
import ChatIcon from "@mui/icons-material/Chat";
import TuneIcon from "@mui/icons-material/Tune";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import PersonIcon from "@mui/icons-material/Person";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import Tooltip from "@mui/material/Tooltip";
import PersonCardModal from "./PersonCardModal";

import WarningIcon from "@mui/icons-material/Warning";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { MenuRow } from "./DateCard2";
import { getCardColor, getCardColor2 } from "../../../../helpers/helpers";
import CircularProgress from "@mui/material/CircularProgress";
import ViolationsModal from "./ViolationsModal";

import { formatName } from "../../../../helpers/helpers";

function PersonCard(props) {
  // Shows modal when set to true
  const [open, setOpen] = React.useState(false);

  // Function for opening the modal
  const handleOpen = () => {
    setOpen(true);
    setIsDropdownOpen(false);
    setMainRoleId(get_main_role_id());
  };

  // Function for closing the modal
  const handleClose = () => setOpen(false);

  const [mainRoleId, setMainRoleId] = React.useState(get_main_role_id());

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [isLoadingSchedule, setIsLoadingSchedule] = React.useState(false);

  function toggleIsDropdownOpen() {
    setIsDropdownOpen((prevState) => !prevState);
  }

  // Ref to the dropdown container
  const dropdownRef = React.useRef(null);

  // Roster rows carry the auth user under `user`; some callers pass `user_id`.
  const personUserId = props.p?.user?.id ?? props.p?.user_id ?? null;

  // Ref to this card's own kebab, so a click on another row's kebab counts
  // as an outside click and closes this menu (only one open at a time).
  const kebabRef = React.useRef(null);

  const handleDocumentClick = (event) => {
    if (
      isDropdownOpen &&
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      !(kebabRef.current && kebabRef.current.contains(event.target))
    ) {
      setIsDropdownOpen(false);
    }
  };

  // Attach click event listener to the document when the dropdown is open
  React.useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener("click", handleDocumentClick);
    } else {
      document.removeEventListener("click", handleDocumentClick);
    }

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [isDropdownOpen]);

  // Function that toggles locked
  function toggleLocked() {
    // console.log(props.id);
    props.lock_person(props.id);
  }

  // Function that gets main role id
  function get_main_role_id() {
    return getEmployeeMainRole(props.p.available_role_info)?.scheduled_role ?? null;
  }

  const handleMainRoleSelection = (event) => {
    setMainRoleId(event.target.value);
  };

  const avatarRoleId = get_main_role_id();

  const returnAvatar = () => {
    return (
      <PersonAvatar
        name={name}
        bgColor={getCardColor2(mainRoleId, props.role_names, false)}
      />
    );
  };

  // function formatName(inputName) {
  //   // Split the input name into parts
  //   const nameParts = inputName.split(" ");

  //   // Capitalize the first letter of each part and convert the rest to lowercase
  //   const formattedNameParts = nameParts.map(
  //     (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  //   );

  //   // Join the formatted parts back together
  //   const formattedName = formattedNameParts.join(" ");

  //   return formattedName;
  // }

  const name = props.p.first_name + " " + props.p.last_name;

  const formattedName = formatName(name);

  // Violations modal

  const [openViolations, setOpenViolations] = React.useState(false);
  const handleOpenViolations = () => {setOpenViolations(true);};
  const handleCloseViolations = () => setOpenViolations(false);

  // Planned violations keep their existing detail modal. Actual cumulative
  // findings appear separately, without assigning them to an arbitrary shift.
  const employeeLaborLawViolations = props.showActualHours
    ? []
    : props.laborLawViolations?.filter((x, i) => x.store_team_member_id === props.p?.id)

  // With the metrics toggle on, the row reads as hours worked over hours
  // scheduled through today, so a partial week is compared like for like.
  // Otherwise it stays on the full week's scheduled hours.
  const hoursLabel = props.showActualHours
    ? `${(props.actualHours || 0).toFixed(2)} / ${(
        props.scheduledHoursToDate || 0
      ).toFixed(2)} hrs`
    : `${props.p.hours.toFixed(2)} hrs`;

  const hoursTooltip = props.showActualHours
    ? "Hours worked so far vs hours scheduled through today at this store"
    : "";

  // Lock and warning indicators: bottom-right in the normal row, inline next
  // to the hours in compact rows so they never collide with the kebab.
  const statusIcons = (
    <>
      {props.showActualHours && props.laborLawEnabled && props.actualEmployeeWarnings?.length > 0 && (
        <Tooltip title={<div className="max-h-64 overflow-y-auto">
          <p className="font-semibold">Actual labor law issues</p>
          <ul className="list-disc pl-4">{props.actualEmployeeWarnings.map((message, index) => <li key={index}>{message}</li>)}</ul>
        </div>}>
          <span tabIndex={0} aria-label="Actual employee labor law issues" className="inline-flex items-center text-rose-600">
            <WarningIcon style={{ fontSize: "14px" }} />
          </span>
        </Tooltip>
      )}
      {props.laborLawEnabled && employeeLaborLawViolations?.length > 0 && (
        <Tooltip title="Click to see employee schedule warnings">
          <div className="cursor-pointer flex items-center" onClick={handleOpenViolations}>
            <WarningIcon style={{ fontSize: "14px", color: "#d32f2f" }} />
          </div>
        </Tooltip>
      )}
      {!props.week_in_past && props.locked_person && (
        <Tooltip
          title={`You have locked ${formatName(
            props.p.first_name
          )}'s schedule for this week. This will prevent any changes to their schedule when the autoscheduler is run. Click to unlock.`}
        >
          <div
            className="text-violet-700 hover:text-violet-900 cursor-pointer flex items-center"
            onClick={toggleLocked}
          >
            <LockIcon style={{ fontSize: "14px" }} />
          </div>
        </Tooltip>
      )}
    </>
  );

  return (
    <div
      className={`w-full h-full ${
        props.highlightedEmployees?.has(props.id) ? "opacity-0" : "opacity-100"
      } duration-1000 ease-in-out`}
    >
      <div className="w-full h-full">
        <div className="flex w-full h-full max-w-sm text-slate-900 relative">
          <div className="flex flex-1 items-center gap-2 min-w-0">
            <div className="cursor-pointer shrink-0" onClick={handleOpen}>
              <PersonAvatar
                name={formattedName}
                bgColor={getCardColor2(avatarRoleId, props.role_names, false)}
                view={props.view}
                size={props.minimizeMode ? "sm" : undefined}
              />
            </div>
            <div className="min-w-0">
              <p
                className="text-xxs font-bold cursor-pointer truncate"
                onClick={handleOpen}
              >
                {formattedName.substring(0, 18)}
              </p>
              <div className="flex items-center space-x-1 min-w-0">
                <Tooltip title={hoursTooltip}>
                  <p
                    className={`${
                      props.minimizeMode ? "text-xxxs" : "text-xxs"
                    } cursor-pointer whitespace-nowrap shrink-0`}
                    onClick={handleOpen}
                  >
                    {hoursLabel}
                  </p>
                </Tooltip>
                {props.minimizeMode && statusIcons}
                {(props.p?.status === "archived" || props.p?.status === "hidden") &&
                  <p
                    className={`${
                      props.minimizeMode ? "text-xxxs" : "text-xxs"
                    } cursor-pointer text-rose-700 font-bold truncate`}
                    onClick={handleOpen}
                  >
                    {props.p?.status === "archived" ? "DELETED" : "HIDDEN"}
                  </p>
                }
              </div>
            </div>
          </div>
          <div className="relative shrink-0">
            <div
              ref={kebabRef}
              className={`more-vert-icon-class w-5 h-6 flex items-center justify-center hover:bg-slate-100 rounded-md cursor-pointer transition-colors duration-150 text-slate-400 hover:text-slate-600 ${
                props.minimizeMode
                  ? "mt-0.5 -mr-0.5"
                  : props.view !== "daily"
                  ? "mt-1.5 -mr-0.5"
                  : "-mt-0.5 -mr-2"
              }`}
              onClick={toggleIsDropdownOpen}
            >
              <MoreVertIcon style={{ fontSize: "18px" }} />
            </div>
            {isDropdownOpen && (
              <div
                className="absolute top-5 left-0 mt-2 w-[300px] rounded-xl shadow-xl bg-white border border-slate-200 z-[100] py-1"
                ref={dropdownRef}
              >
                <MenuRow
                  icon={TuneIcon}
                  label="Roles & Skills"
                  onClick={handleOpen}
                />
                {props.openEmployeeProfile && (
                  <MenuRow
                    icon={PersonIcon}
                    label="Open employee profile"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      props.openEmployeeProfile(props.p.id);
                    }}
                  />
                )}
                {props.openDirectMessageWith &&
                  personUserId &&
                  String(personUserId) !== String(props.currentUserId ?? "") && (
                  <MenuRow
                    icon={ChatIcon}
                    label={`Message ${formatName(props.p.first_name)}`}
                    onClick={() => {
                      setIsDropdownOpen(false);
                      props.openDirectMessageWith({
                        userId: personUserId,
                        name: formatName(props.p.first_name),
                      });
                    }}
                  />
                )}
                {!props.week_already_started && (
                  <MenuRow
                    icon={FolderCopyIcon}
                    label={`Copy ${formatName(props.p.first_name)}'s schedule from last week`}
                    loading={
                      isLoadingSchedule ? (
                        <CircularProgress size="1rem" />
                      ) : null
                    }
                    onClick={() => {
                      setIsLoadingSchedule(true);
                      props.loadEmployeePreviousSchedule(props.p.id);
                    }}
                  />
                )}
                {!props.week_in_past && (
                  <MenuRow
                    icon={props.locked_person ? LockIcon : LockOpenIcon}
                    label={`${formatName(props.p.first_name)}'s schedule locked for week`}
                    active={!!props.locked_person}
                    onClick={toggleLocked}
                  />
                )}
              </div>
            )}
          </div>
          {!props.minimizeMode && (
            <div
              className={`absolute ${
                props.view === "daily" ? "-bottom-2" : "-bottom-1"
              } right-0 flex items-center gap-1.5`}
            >
              {statusIcons}
            </div>
          )}
        </div>
        <PersonCardModal
          open={open}
          handleClose={handleClose}
          p={props.p}
          role_names={props.role_names}
          updatePersonInfo={props.updatePersonInfo}
          handleMainRoleSelection={handleMainRoleSelection}
          mainRoleId={mainRoleId}
          setMainRoleId={setMainRoleId}
          returnAvatar={returnAvatar}
          proficiencyStarsCount={props.proficiencyStarsCount}
          proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
        />
        <ViolationsModal
          open={openViolations}
          handleClose={handleCloseViolations}
          employeeLaborLawViolations={employeeLaborLawViolations}
          p={props.p}
          formattedName={formattedName}
        />
      </div>
    </div>
  );
}

export default PersonCard;
